/**
 * Fill Forms' choices: the copies of forms a run fills, from the server's
 * menu (`GET /api/ws/<id>/form-types`): the forms on the case, else the
 * visa's defaults, plus every form CaseBlink fills. A case can file a form
 * more than once (a G-28 per party), so the picks are copies, not a set:
 * each copy is its own form on the case, told apart by its label.
 */
import type { FormChoice, FormOption } from "./types";

/** One entry of `selected`: a form on the case, or a default not created yet. */
export type SelectedFormDto = {
  form_number: string;
  form_title: string;
  label: string | null;
  form_id: string | null;
};

export type CatalogFormDto = {
  form_number: string;
  form_title: string;
  edition_date?: string | null;
  agency?: string;
};

export type FormTypesResponse = {
  catalog: CatalogFormDto[];
  selected: SelectedFormDto[];
};

/** One copy in the Fill Forms list. */
export type FormPick = {
  /** Stable for the list: the case form's id, else made when it's added. */
  id: string;
  formNumber: string;
  title: string;
  /** The case's form this copy is; null until a run creates it. */
  formId: string | null;
  /** The case form's label; null for a copy not created yet. */
  label: string | null;
  /** What the attorney calls a new copy ("Beneficiary"); its label when set. */
  note: string;
};

/** A form on the case, as a copy is matched to it. */
export type CaseFormRef = {
  id: string;
  formNumber: string;
  label: string | null;
};

/** `picks` is null when the server couldn't say: Casey fills what the visa needs. */
export type FormTypes = { catalog: FormOption[]; picks: FormPick[] | null };

let made = 0;
const newId = () => {
  made += 1;
  return `form-pick-${Date.now().toString(36)}-${made}`;
};

export const formTypesOf = (r: FormTypesResponse): FormTypes => ({
  catalog: (r.catalog ?? []).map((c) => ({
    formNumber: c.form_number,
    title: c.form_title
  })),
  picks: (r.selected ?? []).map((s) => ({
    id: s.form_id ?? newId(),
    formNumber: s.form_number,
    title: s.form_title,
    formId: s.form_id,
    label: s.label,
    note: ""
  }))
});

/** A new copy of a catalog form, at the end of the list. */
export const addFormPick = (
  picks: readonly FormPick[],
  form: FormOption
): FormPick[] => [
  ...picks,
  {
    id: newId(),
    formNumber: form.formNumber,
    title: form.title,
    formId: null,
    label: null,
    note: ""
  }
];

export const removeFormPick = (
  picks: readonly FormPick[],
  id: string
): FormPick[] => picks.filter((p) => p.id !== id);

export const setFormPickNote = (
  picks: readonly FormPick[],
  id: string,
  note: string
): FormPick[] => picks.map((p) => (p.id === id ? { ...p, note } : p));

/** How many copies of each form number are picked. */
export const pickCounts = (
  picks: readonly FormPick[]
): ReadonlyMap<string, number> => {
  const out = new Map<string, number>();
  for (const p of picks)
    out.set(p.formNumber, (out.get(p.formNumber) ?? 0) + 1);
  return out;
};

/** A label as a scope clause and create_form can carry it. */
const cleanLabel = (text: string) =>
  text
    .replace(/[;"()]/g, " ")
    .replace(/\.(\s|$)/g, "$1")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 40);

const fold = (label: string | null) => (label ?? "").trim().toLowerCase();

/**
 * The copies a run fills, each with the label create_form keys it by. A copy
 * on the case keeps its label; a new one takes its note, else none when it's
 * the form's only unlabelled copy, else the next free number ("2"). No two
 * copies of a form share a label, so one is never filled for another.
 */
export const formChoicesOf = (
  picks: readonly FormPick[],
  /** The case's forms: a new copy a run created is that form from then on. */
  caseForms: readonly CaseFormRef[] = []
): FormChoice[] => {
  const used = new Map<string, Set<string>>();
  const usedFor = (n: string) => {
    const key = n.toUpperCase();
    const set = used.get(key) ?? new Set<string>();
    used.set(key, set);
    return set;
  };
  const claimed = new Set<string>();
  for (const p of picks)
    if (p.formId) {
      usedFor(p.formNumber).add(fold(p.label));
      claimed.add(p.formId);
    }
  const counts = pickCounts(picks);
  return picks.map((p) => {
    let label = p.formId ? p.label : cleanLabel(p.note) || null;
    if (!p.formId) {
      const taken = usedFor(p.formNumber);
      if (taken.has(fold(label))) {
        let n = 2;
        const base = label;
        const next = () => (base ? `${base} ${n}` : String(n));
        while (taken.has(fold(next()))) n += 1;
        label = next();
      }
      taken.add(fold(label));
    }
    const several = (counts.get(p.formNumber) ?? 0) > 1;
    const formId =
      p.formId ??
      caseForms.find(
        (f) =>
          f.formNumber.toUpperCase() === p.formNumber.toUpperCase() &&
          fold(f.label) === fold(label) &&
          !claimed.has(f.id)
      )?.id ??
      null;
    if (formId) claimed.add(formId);
    return {
      formNumber: p.formNumber,
      title: p.title,
      formId,
      label,
      name: label
        ? `${p.formNumber} (${label})`
        : several
        ? `${p.formNumber} (1)`
        : p.formNumber
    };
  });
};

/**
 * The label a new copy of `form` takes next to the case's copies, as a run
 * would label it: its note, else none for the form's first copy, else the
 * next free number ("2"), never one an existing copy has.
 */
export const newCopyLabel = (
  caseForms: readonly CaseFormRef[],
  form: FormOption,
  note: string
): string | null => {
  const picks: FormPick[] = caseForms.map((f) => ({
    id: f.id,
    formNumber: f.formNumber,
    title: "",
    formId: f.id,
    label: f.label,
    note: ""
  }));
  const choices = formChoicesOf([
    ...picks,
    {
      id: "new",
      formNumber: form.formNumber,
      title: form.title,
      formId: null,
      label: null,
      note
    }
  ]);
  return choices[choices.length - 1].label;
};

/** "G-28 (1), G-28 (2) and I-129". */
export const formsLine = (forms: readonly FormChoice[]): string => {
  const names = forms.map((f) => f.name);
  return names.length < 2
    ? names.join("")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};
